import { useLocalization } from "@fluent/react";
import { Button } from "@narmi/design_system";
import { PageLayout } from "@lending/components/PageLayout/PageLayout";
import { StatusCard, TimelineStep } from "@lending/components/StatusTimeline";
import { useGetAutoLoanApplicationQuery } from "@lending/hooks/queries/AutoLoanApplication/useGetAutoLoanApplicationQuery";
import { useGetCreditCardApplicationQuery } from "@lending/hooks/queries/CreditCardApplication/useGetCreditCardApplicationQuery";
import { useGetPersonalLoanApplicationQuery } from "@lending/hooks/queries/PersonalLoanApplication/useGetPersonalLoanApplicationQuery";
import { useGetInstitutionQuery } from "@lending/hooks/queries/useGetInstitutionQuery";
import { useFlowType } from "@lending/hooks/useFlowType";
import { useProductType } from "@lending/hooks/useProductType";
import { CROSS_SELL_APPLICATION_UUID_QUERY_PARAM_KEY } from "@lending/utils/loadPrequalApp";
import styles from "./Submitted.module.css";
import type { ProductType } from "@lending/navigation";
import type { StateEnum } from "@lending/types";

const NEGATIVE_TERMINAL_STATES: StateEnum[] = [
  "decisioning_rejected",
  "kyc_rejected",
  "canceled",
  "expired",
  "decisioning_failed",
];

function useApplicationByProductType(productType: ProductType | null) {
  const personalLoanQuery = useGetPersonalLoanApplicationQuery({
    enabled: productType === "personal-loan",
  });
  const creditCardQuery = useGetCreditCardApplicationQuery({
    enabled: productType === "credit-card",
  });
  const autoLoanQuery = useGetAutoLoanApplicationQuery({
    enabled: productType === "auto-loan",
  });

  switch (productType) {
    case "credit-card":
      return { application: creditCardQuery.data, isLoading: creditCardQuery.isLoading };
    case "auto-loan":
      return { application: autoLoanQuery.data, isLoading: autoLoanQuery.isLoading };
    default:
      return { application: personalLoanQuery.data, isLoading: personalLoanQuery.isLoading };
  }
}

function isNegativeTerminalState(state?: StateEnum): boolean {
  if (!state) return false;
  return NEGATIVE_TERMINAL_STATES.includes(state);
}

function Submitted() {
  const { l10n } = useLocalization();
  const productType = useProductType();
  const flowType = useFlowType();
  const { institution } = useGetInstitutionQuery();
  const { application, isLoading } = useApplicationByProductType(productType);
  const searchParams = new URLSearchParams(window.location.search);

  const isOlb = flowType === "olb";
  const isCrossSell = searchParams.has(CROSS_SELL_APPLICATION_UUID_QUERY_PARAM_KEY);
  const applicationState = application?.state;
  const primaryApplicantFirstName = application?.primary_applicant?.first_name?.trim();
  const isNegativeState = isNegativeTerminalState(applicationState);
  const showTimeline = Boolean(applicationState) && !isNegativeState;

  const statusContent = !isLoading && (
    <>
      {!showTimeline ? (
        <StatusCard
          title={
            productType === "credit-card"
              ? l10n.getString(
                  "submitted-status-fallback-title-credit-card",
                  null,
                  "Credit card application received"
                )
              : productType === "auto-loan"
                ? l10n.getString(
                    "submitted-status-fallback-title-auto-loan",
                    null,
                    "Application received"
                  )
                : l10n.getString(
                    "submitted-status-fallback-title-personal-loan",
                    null,
                    "Application received"
                  )
          }
          description={
            productType === "credit-card"
              ? l10n.getString(
                  "submitted-status-fallback-description-credit-card",
                  null,
                  "We're reviewing your application and will keep you updated every step of the way."
                )
              : productType === "auto-loan"
                ? l10n.getString(
                    "submitted-status-fallback-description-auto-loan",
                    null,
                    "A loan officer will be in touch to complete processing your application."
                  )
                : l10n.getString(
                    "submitted-status-fallback-description-personal-loan",
                    null,
                    "A loan officer will be in touch to complete processing your application."
                  )
          }
        />
      ) : (
        <div className={styles.timeline}>
          <TimelineStep
            title={
              productType === "credit-card"
                ? l10n.getString(
                    "submitted-status-step-1-title-credit-card",
                    null,
                    "Credit card application received"
                  )
                : productType === "auto-loan"
                  ? l10n.getString(
                      "submitted-status-step-1-title-auto-loan",
                      null,
                      "Application received"
                    )
                  : l10n.getString(
                      "submitted-status-step-1-title-personal-loan",
                      null,
                      "Application received"
                    )
            }
            description={
              productType === "credit-card"
                ? l10n.getString(
                    "submitted-status-step-1-description-credit-card",
                    null,
                    "We're reviewing your application and will keep you updated every step of the way."
                  )
                : productType === "auto-loan"
                  ? l10n.getString(
                      "submitted-status-step-1-description-auto-loan",
                      null,
                      "We're reviewing your application and will keep you updated."
                    )
                  : l10n.getString(
                      "submitted-status-step-1-description-personal-loan",
                      null,
                      "We're reviewing your application and will keep you updated."
                    )
            }
            isComplete
            isPrimary
            showLine
            stepNumber={1}
          />

          <TimelineStep
            title={
              productType === "credit-card"
                ? l10n.getString(
                    "submitted-status-step-2-title-credit-card",
                    null,
                    "Credit card issued"
                  )
                : productType === "auto-loan"
                  ? l10n.getString("submitted-status-step-2-title-auto-loan", null, "Loan review")
                  : l10n.getString(
                      "submitted-status-step-2-title-personal-loan",
                      null,
                      "Loan review"
                    )
            }
            description={
              productType === "credit-card"
                ? l10n.getString(
                    "submitted-status-step-2-description-credit-card",
                    null,
                    "Receive your credit card in the mail and start using it immediately."
                  )
                : productType === "auto-loan"
                  ? l10n.getString(
                      "submitted-status-step-2-description-auto-loan",
                      null,
                      "A loan officer will reach out to finalize your loan terms."
                    )
                  : l10n.getString(
                      "submitted-status-step-2-description-personal-loan",
                      null,
                      "A loan officer will reach out to finalize your loan terms."
                    )
            }
            estimatedTime={
              productType === "credit-card"
                ? l10n.getString("submitted-status-step-2-time-credit-card", null, "2-3 days")
                : productType === "auto-loan"
                  ? l10n.getString("submitted-status-step-2-time-auto-loan", null, "2-3 days")
                  : l10n.getString("submitted-status-step-2-time-personal-loan", null, "2-3 days")
            }
            isComplete={false}
            showLine={productType !== "credit-card"}
            stepNumber={2}
          />

          {productType === "auto-loan" && (
            <TimelineStep
              title={l10n.getString(
                "submitted-status-step-3-title-auto-loan",
                null,
                "Funds received"
              )}
              description={l10n.getString(
                "submitted-status-step-3-description-auto-loan",
                null,
                "Funds will be deposited to your account within 24 hours of approval."
              )}
              estimatedTime={l10n.getString(
                "submitted-status-step-3-time-auto-loan",
                null,
                "3-5 days"
              )}
              isComplete={false}
              stepNumber={3}
            />
          )}
          {productType !== "auto-loan" && productType !== "credit-card" && (
            <TimelineStep
              title={l10n.getString(
                "submitted-status-step-3-title-personal-loan",
                null,
                "Funds received"
              )}
              description={l10n.getString(
                "submitted-status-step-3-description-personal-loan",
                null,
                "Funds will be deposited to your account within 24 hours of approval."
              )}
              estimatedTime={l10n.getString(
                "submitted-status-step-3-time-personal-loan",
                null,
                "3-5 days"
              )}
              isComplete={false}
              stepNumber={3}
            />
          )}
        </div>
      )}

      {isOlb && !isCrossSell && (
        <div className={styles.returnButton}>
          <Button
            as="a"
            kind="tonal"
            size="s"
            label={l10n.getString("submitted-status-return-to-banking", null, "Return to banking")}
            type="button"
            href={institution.lending_return_to_banking_url}
          />
        </div>
      )}
    </>
  );

  return (
    <PageLayout isLoading={isLoading} className={styles.pageLayout}>
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.header}>
            <h1 className="fontFamily--heading">
              {isNegativeState
                ? l10n.getString(
                    "submitted-status-fallback-heading",
                    null,
                    "Update on your application"
                  )
                : primaryApplicantFirstName
                  ? l10n.getString(
                      "submitted-status-heading-personalized",
                      { firstName: primaryApplicantFirstName },
                      `You're all set, ${primaryApplicantFirstName}`
                    )
                  : l10n.getString("submitted-status-heading", null, "You're all set")}
            </h1>
          </div>

          {statusContent}
        </div>
      </div>
    </PageLayout>
  );
}

export { Submitted };
